import { useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, type FormEvent, useState } from 'react';
import { Alert, Button, Card, Field } from '../../components/ui';
import { t } from '../../i18n';
import { api, downloadFile } from '../../lib/api';
import { ATTACHMENT_ACCEPT, formatSize, uploadAttachment } from '../../lib/attachments';
import { errorMessage } from '../../lib/errors';
import { formatDay, type ProjectDetail, type ProjectDocument } from './data';

/**
 * Documentos del proyecto con versiones (guía 7.2): cada versión es un adjunto del proyecto; se descarga la última y
 * se ve el historial. Suben versiones quien gestiona el proyecto y su equipo.
 */
export function ProjectDocuments({ project }: { project: ProjectDetail }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canWrite = project.can.contribute;

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ['proyectos', 'detalle'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const create = (e: FormEvent) => {
    e.preventDefault();
    void act(async () => {
      await api(`/api/proyectos/${project.number}/documentos`, { json: { title: title.trim() } });
      setTitle('');
    });
  };

  return (
    <Card title={t('pr.documentos')}>
      <div className="space-y-3">
        {error && <Alert>{error}</Alert>}
        {project.documents.length === 0 ? (
          <p className="text-sm text-slate-500">{t('pr.documentos_vacio')}</p>
        ) : (
          <ul className="divide-y divide-slate-100" aria-label={t('pr.documentos')}>
            {project.documents.map((d) => (
              <DocumentRow key={d.id} doc={d} projectId={project.id} canWrite={canWrite} busy={busy} act={act} />
            ))}
          </ul>
        )}
        {canWrite && (
          <form onSubmit={create} className="flex items-end gap-2">
            <div className="flex-1">
              <Field id="pr-doc-titulo" label={t('pr.documento_nuevo')} maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <Button type="submit" variant="secondary" disabled={busy || !title.trim()}>
              {t('pr.agregar')}
            </Button>
          </form>
        )}
      </div>
    </Card>
  );
}

function DocumentRow({
  doc,
  projectId,
  canWrite,
  busy,
  act,
}: {
  doc: ProjectDocument;
  projectId: string;
  canWrite: boolean;
  busy: boolean;
  act: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [history, setHistory] = useState(false);
  const [note, setNote] = useState('');
  const latest = doc.versions[0];
  const upload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    void act(async () => {
      // El archivo se sube como adjunto del proyecto y se registra como la versión siguiente del documento.
      const a = await uploadAttachment('project', projectId, file);
      await api(`/api/proyectos/documentos/${doc.id}/versiones`, { json: { attachmentId: a.id, note: note.trim() || null } });
      setNote('');
    });
  };

  return (
    <li className="space-y-1 py-2 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-slate-900">{doc.title}</span>
        {latest ? (
          <Button variant="link" className="text-xs" onClick={() => void act(() => downloadFile(`/api/adjuntos/${latest.attachmentId}/descarga`, latest.fileName))}>
            {t('pr.descargar_version', { version: latest.version })}
          </Button>
        ) : (
          <span className="text-xs text-slate-400">{t('pr.sin_versiones')}</span>
        )}
      </div>
      {latest && (
        <p className="text-xs text-slate-500">
          {latest.fileName} · {formatSize(latest.sizeBytes)} · {latest.createdByName ?? '—'} · {formatDay(latest.createdAt)}
          {latest.note ? ` · ${latest.note}` : ''}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        {doc.versions.length > 1 && (
          <Button variant="link" className="text-xs" aria-expanded={history} onClick={() => setHistory(!history)}>
            {history ? t('pr.ocultar_historial') : t('pr.ver_historial', { n: doc.versions.length })}
          </Button>
        )}
        {canWrite && (
          <>
            <input
              className="min-w-40 flex-1 rounded border border-slate-300 px-2 py-0.5 text-xs"
              placeholder={t('pr.nota_version')}
              aria-label={t('pr.nota_version_de', { documento: doc.title })}
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <label className="cursor-pointer text-xs text-marca hover:underline">
              {t('pr.subir_version')}
              <input type="file" className="sr-only" accept={ATTACHMENT_ACCEPT} disabled={busy} aria-label={t('pr.subir_version_de', { documento: doc.title })} onChange={upload} />
            </label>
          </>
        )}
      </div>
      {history && (
        <ol className="ml-3 space-y-0.5 border-l border-slate-200 pl-3 text-xs text-slate-600">
          {doc.versions.map((v) => (
            <li key={v.version}>
              <Button variant="link" className="text-xs" onClick={() => void act(() => downloadFile(`/api/adjuntos/${v.attachmentId}/descarga`, v.fileName))}>
                v{v.version}
              </Button>{' '}
              {v.fileName} · {v.createdByName ?? '—'} · {formatDay(v.createdAt)}
              {v.note ? ` · ${v.note}` : ''}
            </li>
          ))}
        </ol>
      )}
    </li>
  );
}
